import { createContext, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext({
  theme: "light",
  resolved: "light",
  cycle: () => {},
  setTheme: () => {},
});
const STORAGE_KEY = "hd_theme"; // stores "light" | "dark" | "auto"
const VALID = ["light", "dark", "auto"];

const systemPrefersDark = () =>
  typeof window !== "undefined"
  && window.matchMedia
  && window.matchMedia("(prefers-color-scheme: dark)").matches;

function applyClass(resolved) {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (VALID.includes(saved)) return saved;
    } catch (err) {
      console.warn("localStorage unavailable (incognito/restricted browser?):", err);
    }
    return "auto";
  });

  // Track system preference so 'auto' can react in real time.
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (e) => setSystemDark(e.matches);
    mq.addEventListener?.("change", listener) || mq.addListener?.(listener);
    return () => {
      mq.removeEventListener?.("change", listener) || mq.removeListener?.(listener);
    };
  }, []);

  const resolved = useMemo(() => {
    if (theme === "auto") return systemDark ? "dark" : "light";
    return theme;
  }, [theme, systemDark]);

  useEffect(() => { applyClass(resolved); }, [resolved]);
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, theme); } catch (err) { console.warn("localStorage.setItem failed:", err); } }, [theme]);

  const setTheme = (t) => setThemeState(VALID.includes(t) ? t : "auto");
  const cycle = () => setThemeState((t) => (t === "light" ? "dark" : t === "dark" ? "auto" : "light"));

  const value = useMemo(
    () => ({ theme, resolved, cycle, setTheme }),
    [theme, resolved]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
